/**
 * The technique registry, in escalating order.
 *
 * The order *is* the difficulty model: the solve loop always applies the
 * easiest available deduction, so the hardest technique a solve ever needed is
 * a fair statement of how hard the puzzle is. Ordering follows the Sudoku
 * Explainer ratings so it is defensible to players who already know the scale.
 */

import { hiddenSingle, nakedSingle } from "./singles";
import { claiming, pointing } from "./locked";
import {
  hiddenPair,
  hiddenQuad,
  hiddenTriple,
  nakedPair,
  nakedQuad,
  nakedTriple,
} from "./subsets";
import { jellyfish, swordfish, xWing } from "./fish";
import { xyWing, xyzWing } from "./wings";
import { xyChain } from "./chains";
import type { Technique, TechniqueId, Tier } from "./types";

export const TECHNIQUES: readonly Technique[] = [
  hiddenSingle, // 1.5
  nakedSingle, //  2.3
  pointing, //     2.6
  claiming, //     2.8
  nakedPair, //    3.0
  xWing, //        3.2
  hiddenPair, //   3.4
  nakedTriple, //  3.6
  swordfish, //    3.8
  hiddenTriple, // 4.0
  xyWing, //       4.2
  xyzWing, //      4.4
  nakedQuad, //    5.0
  jellyfish, //    5.2
  hiddenQuad, //   5.4
  xyChain, //      6.6
];

export const TECHNIQUE_BY_ID: ReadonlyMap<TechniqueId, Technique> = new Map(
  TECHNIQUES.map((technique) => [technique.id, technique]),
);

/** Easiest to hardest. */
export const TIER_ORDER: readonly Tier[] = ["easy", "medium", "hard", "expert", "evil"];

export * from "./types";
export {
  claiming,
  hiddenPair,
  hiddenQuad,
  hiddenSingle,
  hiddenTriple,
  jellyfish,
  nakedPair,
  nakedQuad,
  nakedSingle,
  nakedTriple,
  pointing,
  swordfish,
  xWing,
  xyChain,
  xyWing,
  xyzWing,
};
